import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ReactHtmlParser from 'react-html-parser';

import PlayButton from '../../../Core/PlayButton';
import VideoOverlay from '../../../Core/VideoOverlay';
import Hubspot from '../../Admin/Hubspot';

import {
  Wrapper,
  StyledContainer,
  Header,
  Body,
  VideoWrapper,
  StyledVideo,
  ImageWrapper,
  StyledImage,
  Content,
  // Title,
  List,
  Styledlink,
  StyledBottomLink,
  ListItem,
} from './styles';

// * video {obj} the data for the video if displayed
// * image {obj} the data for the image if displayed
// * subheading {RichText} subheading for the section
// * subheadingLocation {String} If 'Top', subheaading for the section, not list items
// * items {array} each list item that should display beside the asset
// * title {RichText} title of the section
// * colourOverRideTrext {string} A value of Light or Dark that determines what style of text we should use
// * colourOverrideBackground {string} A hexvalue of what the bg color should be
// * formId {string} to check if the hubspot form should display or not
// * ctaLink {obj} contains the url for the link
// * ctaLocation {bool} If false, cta should show in the list, if true, at the bottom of the component
// * ctaText {string} innerText of a CTA
// * ctaType {string} className to control CTA style
// * mediaPosition {string} className to control what side the asset is on
// * mediaType {string} to check if a video or img should display

const VideoBanner = ({ data, className }) => {
  if (!data) return null;
  const [playing, setPlaying] = useState(false);

  const {
    video,
    image,
    imageSharp,
    subheadingHtml,
    subheadingOverListHtml,
    items,
    titleHtml,
    colourOverrideText,
    colourOverrideBackground,
    formId,
    ctaLink,
    ctaLocation,
    ctaText,
    ctaType,
    mediaPosition,
    mediaFixedPosition,
    ariaLabel,
    queryString,
    lang,
  } = data;

  const textColor = colourOverrideText
    ? `text-${colourOverrideText.toLowerCase()}`
    : 'text-Light';

  const position = mediaPosition ? `${mediaPosition.toLowerCase()}` : '';

  const imagePosition =
    mediaFixedPosition === 'Image Fixed to Bottom' ? 'bottom' : '';

  const mediaType = data.video.embedURL ? 'Video' : 'Image';

  return (
    <Wrapper
      className={[className, textColor, imagePosition]}
      colourOverrideBackground={colourOverrideBackground}
    >
      <StyledContainer className="auto-margin">
        <Header>
          {ReactHtmlParser(titleHtml)}
          {ReactHtmlParser(subheadingHtml)}
        </Header>
        <Body className={position}>
          {mediaType === 'Video' ? (
            <VideoWrapper>
              {!playing ? (
                <VideoOverlay
                  thumbnail={imageSharp.publicURL}
                  onClick={() => setPlaying(true)}
                  bgPosition="top left"
                >
                  <PlayButton onClick={() => setPlaying(true)} />
                </VideoOverlay>
              ) : (
                <StyledVideo
                  videoSrcURL={video.embedURL}
                  frame={video.html}
                  playing={playing}
                />
              )}
            </VideoWrapper>
          ) : (
            <ImageWrapper>
              {imageSharp && <StyledImage src={imageSharp} alt={image.altTag} />}
            </ImageWrapper>
          )}
          <Content>
            {ReactHtmlParser(subheadingOverListHtml)}

            {/* Don't render empty list, as negative margins in IE css break layout with empty list items */}
            {Array.isArray(items) && items.length !== 0 && (
              <List className="no-bullets">
                {items.map(({ groupHtml, imageSharp: iconSharp }, index) => {
                  return (
                    <li
                      key={`${ReactHtmlParser(
                        titleHtml
                      )}-icon-list-${index.toString()}`}
                    >
                      <ListItem image={iconSharp ? iconSharp.publicURL : null}>
                        {ReactHtmlParser(groupHtml)}
                      </ListItem>
                    </li>
                  );
                })}
              </List>
            )}
            {ctaLink && ctaText && !ctaLocation && !formId && (
              // If a list of items exist, render the styled link with css that accounts for list, by using prop
              <Styledlink
                to={`${ctaLink.url}/`}
                className={ctaType}
                ariaLabel={ariaLabel}
                queryString={queryString}
                // If an array of items exist such that a list will be rendered, use the correct styling
                // When slice is created on Prismic, there is always an empty item. So length is at least 1
                withList={!!(Array.isArray(items) && items.length > 1)}
                lang={lang}
              >
                {ctaText}
              </Styledlink>
            )}
            {ctaLink && ctaText && !ctaLocation && formId && (
              <Hubspot
                uuid={formId}
                data={ctaText}
                className={`${ctaType} auto-size`}
              />
            )}
          </Content>
        </Body>
        {/* Optional CTA at bottom of section */}
        {ctaLink && ctaText && ctaLocation && !formId && (
          <StyledBottomLink
            to={ctaLink.url}
            className={ctaType}
            ariaLabel={ariaLabel}
            queryString={queryString}
            lang={lang}
          >
            {ctaText}
          </StyledBottomLink>
        )}
        {/* Optional hubspot form instead of CTA at bottom of section */}
        {ctaLink && ctaText && ctaLocation && formId && (
          <Hubspot
            uuid={formId}
            data={ctaText}
            className={`${ctaType} hubspot-margin`}
          />
        )}
      </StyledContainer>
    </Wrapper>
  );
};

VideoBanner.propTypes = {
  data: PropTypes.shape().isRequired,
  className: PropTypes.string,
};

VideoBanner.defaultProps = {
  className: null,
};

export default VideoBanner;
